/**
 * PropertyTypeEditForm — inline edit form for "create custom property" actions.
 *
 * Reuses the shared PropertyFormFields component (the same form used by the
 * CRM property creation flow) instead of the generic CreateActionEditForm's
 * schema-driven field list, which doesn't understand property-type metadata
 * (field type, options, unique constraint).
 *
 * Maps between the action payload's CRM property keys (ahoy_label,
 * ahoy_field_type_name, ahoy_options, etc.) and PropertyFormFields' value
 * shape. Save/cancel handlers are registered into ActionEditContext so the
 * parent ActionAccordion drives the footer buttons.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import type { PropertyFormValues } from '@/lib/crm/components/properties/PropertyFormFields';
import { PropertyFormFields } from '@/lib/crm/components/properties/PropertyFormFields';
import { useFieldTypes } from '@/lib/crm/hooks/useFieldTypes';
import { getFieldTypeItems } from '@/lib/crm/utils/fieldTypeHelpers';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import type { SelectOption } from '@/pages/dashboard/profile/components/SelectOptionsEditor';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { useActionEdit } from './ActionAccordion';
import {
  extractProperties,
  formatFieldTypeLabel,
  wrapProperties,
} from './CreateActionCard.helpers';
import { useActionPayloadSave } from './shared';
import { SaveValidationError } from './shared/SaveValidationError';

// ─── Payload ↔ form value conversion ──────────────────────────────────────

/**
 * The CRM backend stores select types as "select" while the AI action payload
 * may use "single_select" or "multi_select". Normalize to the form's vocabulary.
 */
function normalizeFieldType(raw: string): string {
  if (raw === 'single_select' || raw === 'multi_select') {
    return 'select';
  }
  return raw;
}

function parseOptions(raw: unknown): SelectOption[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item: unknown) => {
      // Select-option shape { value, label } from the type picker — NOT a serialized CRM property wrapper; do not route through unwrapPropertyValue.
      if (
        typeof item === 'object' &&
        item !== null &&
        'label' in item &&
        'value' in item
      ) {
        return {
          label: String((item as { label: unknown }).label),
          value: String((item as { value: unknown }).value),
        };
      }
      if (typeof item === 'string') {
        return { label: item, value: item };
      }
      return null;
    })
    .filter((o): o is SelectOption => o !== null);
}

function payloadToFormValues(
  flat: Record<string, unknown>
): PropertyFormValues {
  return {
    label: typeof flat.ahoy_label === 'string' ? flat.ahoy_label : '',
    description:
      typeof flat.ahoy_description === 'string' ? flat.ahoy_description : '',
    fieldType: normalizeFieldType(
      typeof flat.ahoy_field_type_name === 'string'
        ? flat.ahoy_field_type_name
        : 'text'
    ),
    options: parseOptions(flat.ahoy_options),
    isUnique:
      flat.ahoy_is_unique_value === true ||
      flat.ahoy_is_unique_value === 'true',
  };
}

function formValuesToPayload(
  values: PropertyFormValues,
  originalFlat: Record<string, unknown>
): Record<string, unknown> {
  // Start from the original payload so we preserve fields not shown in the
  // form (ahoy_type_name, ahoy_entity_type_name, ahoy_is_required, etc.)
  const result = { ...originalFlat };
  result.ahoy_label = values.label;
  result.ahoy_description = values.description || undefined;
  result.ahoy_field_type_name = values.fieldType;
  result.ahoy_options =
    values.fieldType === 'select' && values.options.length > 0
      ? values.options
      : undefined;
  result.ahoy_is_unique_value = values.isUnique;
  return result;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface PropertyTypeEditFormProps {
  action: ProposedAction<ObjectCreatedEntityPayload>;
  proposalId?: number;
  orgId?: number;
  onUpdate?: ActionRendererProps['onUpdate'];
}

export function PropertyTypeEditForm({
  action,
  proposalId,
  orgId,
  onUpdate,
}: PropertyTypeEditFormProps) {
  const { setDirty, saveHandlerRef, cancelHandlerRef } = useActionEdit();
  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });

  const originalFlat = useMemo(
    () => extractProperties(action.payload.payload.properties),
    [action.payload.payload.properties]
  );

  const initialValues = useMemo(
    () => payloadToFormValues(originalFlat),
    [originalFlat]
  );

  const [values, setValues] = useState<PropertyFormValues>(initialValues);

  const optionsError =
    values.fieldType === 'select' && values.options.length < 2
      ? 'Add 2 or more options to save changes.'
      : null;

  // Sync when source payload changes (e.g. after save)
  useEffect(() => {
    setValues(payloadToFormValues(originalFlat));
  }, [originalFlat]);

  // Field types for the type selector. When the schema API hasn't loaded
  // yet, synthesize a single-item list from the payload's field type so the
  // current value is always visible in the selector.
  const { data: fieldTypes, isLoading: fieldTypesLoading } = useFieldTypes();
  const fieldTypeItems = useMemo(() => {
    const items = getFieldTypeItems(fieldTypes ?? []);
    if (items.length > 0) {
      return items;
    }
    // Fallback: show at least the current field type
    const current = values.fieldType;
    if (current) {
      return [
        {
          id: current,
          label: formatFieldTypeLabel(current),
          icon: getPropertyTypeIconComponent({ field_type_name: current }),
        },
      ];
    }
    return items;
  }, [fieldTypes, values.fieldType]);

  // Dirty state
  const isDirty = useMemo(() => {
    return JSON.stringify(values) !== JSON.stringify(initialValues);
  }, [values, initialValues]);

  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  // Save handler — updates the action payload via the proposal API
  const save = useCallback(async () => {
    if (optionsError) {
      throw new SaveValidationError(optionsError);
    }
    const updatedFlat = formValuesToPayload(values, originalFlat);
    const updatedProperties = wrapProperties(updatedFlat);
    const updatedPayload: ObjectCreatedEntityPayload = {
      ...action.payload,
      payload: {
        ...action.payload.payload,
        properties: updatedProperties,
      },
    };
    await savePayload(updatedPayload);
  }, [optionsError, values, originalFlat, action, savePayload]);

  // Cancel handler — resets to original values
  const cancel = useCallback(() => {
    setValues(initialValues);
  }, [initialValues]);

  useLayoutEffect(() => {
    saveHandlerRef.current = save;
    cancelHandlerRef.current = cancel;
  });

  const handleChange = useCallback((newValues: PropertyFormValues) => {
    setValues(newValues);
  }, []);

  return (
    <div className='px-4 py-3'>
      <PropertyFormFields
        values={values}
        onChange={handleChange}
        fieldTypeItems={fieldTypeItems}
        fieldTypesLoading={fieldTypesLoading}
      />
      {optionsError && (
        <p className='text-sm text-error-600 mt-1'>{optionsError}</p>
      )}
    </div>
  );
}
